import { AdminPageHeader } from '@/components/patterns/admin-page'
import { RecordHistoryActionMenu } from '@/components/audit-log/RecordHistoryMenu'
import UserForm from '@/components/forms/UserForm'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { roleQueries } from '@/lib/services/roles/roles.query'
import { userQueries, useUser } from '@/lib/services/users/users.query'
import { userSchema } from '@/lib/services/users/users.schema'
import { loadRouteDataOrNotFound } from '@/lib/route-loader'
import {
  canAccessUsersInOrganization,
  canEditUsersInOrganization,
  canViewRolePermissions,
} from '@/lib/user-access'
import { createFileRoute, notFound, useParams } from '@tanstack/react-router'
import { User } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/users/$userId/')({
  component: RouteComponent,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => userSchema.pick({ userId: true }).parse(params),
  },
  loader: async ({ context, params }) => {
    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )
    const isEditingSelf = +params.userId === context.authState?.user?.id

    if (!canAccessUsersInOrganization(context.ability, organization) && !isEditingSelf)
      throw notFound()

    const user = await loadRouteDataOrNotFound(
      () =>
        context.queryClient.ensureQueryData(
          userQueries.show({
            organizationId: params.orgId,
            userId: params.userId,
          })
        ),
      notFound
    )

    if (!isEditingSelf && canViewRolePermissions(context.ability)) {
      void context.queryClient.ensureQueryData(roleQueries.index({ organizationId: params.orgId }))
    }

    return {
      user,
      organization,
      isReadOnly:
        !isEditingSelf &&
        !(
          user.management_capabilities?.edit_profile ??
          canEditUsersInOrganization(context.ability, organization)
        ),
    }
  },
})

function RouteComponent() {
  const { userId } = useParams({ strict: false })
  const { organization } = Route.useLoaderData()
  const { data: user } = useUser(userId)
  const path = usePathHelper()
  const historyLink = path.users.link('$userId/history', userId)

  return (
    <div className="max-w-5xl space-y-4">
      <AdminPageHeader
        icon={User}
        title={user.name}
        actions={<RecordHistoryActionMenu link={historyLink} triggerLabel="More user actions" />}
      />
      <UserForm user={user} organization={organization} />
    </div>
  )
}
